import { useCallback, useMemo, useRef } from 'react';
import useSWR from 'swr';
import { api } from '../services/api';
import { useLocation } from '../contexts/LocationContext';

export const RECENT_EVENTS_REFRESH_INTERVAL = 60_000;
export const RECENT_EVENTS_FOCUS_THROTTLE = 30_000;
// Test Mode re-reads this much before its newest event: chips upload late (retries,
// SD backlog) with their own event times, so a strict "since newest" would miss them.
export const LIVE_REFRESH_OVERLAP_MS = 10 * 60_000;
// The API only accepts deltas within the last hour; older starts do a full read.
const LIVE_REFRESH_MAX_SINCE_MS = 50 * 60_000;

// New events win over old copies of the same eventId; drop events outside the range.
export function mergeRecentEvents(current, incoming, hours, now = Date.now()) {
  const byId = new Map();
  for (const event of current) byId.set(event.eventId, event);
  for (const event of incoming) byId.set(event.eventId, event);
  const oldest = now - hours * 3_600_000;
  return [...byId.values()]
    .filter(event => event.timestamp >= oldest)
    .sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * Shared recent-event read model for live usage pages.
 *
 * The key includes tenant, location, and range so a selection change cannot
 * reuse another scope's response. SWR revalidates on focus/reconnect and
 * polls only while the page is visible; keepPreviousData lets the UI retain
 * the last good table while a refresh is in flight or fails.
 */
export function useRecentEvents({ hours = 24, enabled = true, live = false } = {}) {
  const testInstanceId = useRef(Math.random().toString(36).slice(2));
  const {
    selectedOrgId,
    selectedLocationId,
    locationsLoadedForOrgId,
    loading: locationLoading
  } = useLocation();

  const contextReady = Boolean(
    enabled &&
      selectedOrgId &&
      !locationLoading &&
      (!selectedOrgId || locationsLoadedForOrgId == null || locationsLoadedForOrgId === selectedOrgId)
  );

  const key = useMemo(
    () => (contextReady
      ? ['recent-events', selectedOrgId, selectedLocationId || 'all', hours,
        ...(process.env.NODE_ENV === 'test' ? [testInstanceId.current] : [])]
      : null),
    [contextReady, selectedOrgId, selectedLocationId, hours]
  );

  const fetchSnapshot = useCallback(async (forceRefresh = false) => {
    const filters = {
      orgId: selectedOrgId,
      locationId: selectedLocationId,
      forceRefresh
    };
    if (typeof api.getRecentEventsSnapshot === 'function') {
      return api.getRecentEventsSnapshot(hours, filters);
    }
    const events = await api.getRecentEvents(hours, filters);
    return { events, generatedAt: new Date().toISOString(), count: events.length };
  }, [hours, selectedOrgId, selectedLocationId]);

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    key,
    () => fetchSnapshot(false),
    {
      // While live (Test Mode) refreshLive keeps the data current; a normal poll
      // would swap it for the up-to-5-min-old server cache.
      refreshInterval: process.env.NODE_ENV === 'test' || live ? 0 : RECENT_EVENTS_REFRESH_INTERVAL,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      revalidateOnFocus: !live,
      revalidateOnReconnect: true,
      focusThrottleInterval: RECENT_EVENTS_FOCUS_THROTTLE,
      dedupingInterval: 5_000,
      keepPreviousData: true,
      errorRetryCount: process.env.NODE_ENV === 'test' ? 0 : 3,
      errorRetryInterval: attempt => Math.min(30_000, 1_000 * (2 ** attempt))
    }
  );

  const refresh = useCallback(
    async () => {
      const snapshot = await fetchSnapshot(true);
      return mutate(snapshot, { revalidate: false });
    },
    [fetchSnapshot, mutate]
  );

  // Test Mode: fetch only the last few minutes and merge them in, instead of
  // re-reading the whole range every 2 s. Reads the latest data through a ref
  // because Test Mode's interval keeps the callback from when it started.
  const dataRef = useRef(data);
  dataRef.current = data;
  const refreshLive = useCallback(
    async () => {
      const current = dataRef.current;
      const newest = current?.events?.length ? current.events[0].timestamp : null;
      const now = Date.now();
      if (!newest || newest < now - LIVE_REFRESH_MAX_SINCE_MS) return refresh();
      const delta = await api.getRecentEventsSnapshot(hours, {
        orgId: selectedOrgId,
        locationId: selectedLocationId,
        sinceMs: newest - LIVE_REFRESH_OVERLAP_MS
      });
      const latest = dataRef.current || current;
      const events = mergeRecentEvents(latest.events || [], delta.events, hours, now);
      return mutate(
        { ...latest, events, count: events.length, generatedAt: delta.generatedAt },
        { revalidate: false }
      );
    },
    [hours, selectedOrgId, selectedLocationId, refresh, mutate]
  );

  const lastSuccessfulAt = useMemo(
    () => (data?.generatedAt ? new Date(data.generatedAt) : null),
    [data?.generatedAt]
  );

  return {
    events: data?.events || [],
    generatedAt: data?.generatedAt || null,
    count: data?.count ?? 0,
    error: error || null,
    isLoading: Boolean(isLoading),
    isValidating: Boolean(isValidating),
    lastSuccessfulAt,
    refresh,
    refreshLive,
    mutate
  };
}

export default useRecentEvents;
